import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import { RangeCalendar, RangeValue } from '../RangeCalendar';
import { Button } from '../button';
import { Tabs } from '../ui';
import { computeBlockedDateSets } from '../../helpers/bookedSlots';
import {
  MAX_MONTHS,
  MIN_MONTHS,
  MONTH_DAYS,
  TripMode,
  daysBetween,
  maxMonthsFrom,
  monthlyEnd,
  monthlyProblem,
  monthsLabel,
  roundToMonths,
  tierOf,
} from '../../helpers/monthlyTrip';

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background-color: rgb(15 23 42 / 0.45);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  transition:
    visibility 0.2s ease-out,
    opacity 0.2s ease-out;
  visibility: ${props => (props.$isOpen ? 'visible' : 'hidden')};
  opacity: ${props => (props.$isOpen ? 1 : 0)};
  pointer-events: ${props => (props.$isOpen ? 'auto' : 'none')};
`;

const ModalContent = styled.div<{ $wide?: boolean }>`
  ${tw`flex flex-col w-full`}
  max-width: ${({ $wide }) => ($wide ? '880px' : '440px')};
  max-height: 92vh;
  background: var(--color-bg-elevated);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-overlay);
  overflow: hidden;
`;

const Header = styled.div`
  ${tw`flex items-center justify-between px-5 pt-4 pb-2`}
`;

const Title = styled.h2`
  ${tw`m-0 text-lg font-semibold`}
  font-family: var(--font-display);
  color: var(--color-text-primary);
`;

const CloseButton = styled.button`
  ${tw`w-9 h-9 inline-flex items-center justify-center rounded-md`}
  color: var(--color-text-secondary);
  &:hover {
    background: var(--color-bg-secondary);
    color: var(--color-text-primary);
  }
`;

/* Pickup → Return summary, like the Flutter sheet's strip. */
const Summary = styled.div`
  ${tw`grid items-center gap-2 mx-5 mb-3 px-4 py-3`}
  grid-template-columns: 1fr auto 1fr;
  background: var(--color-bg-secondary);
  border-radius: var(--radius-md);
`;

const SummaryCol = styled.div<{ $align?: 'left' | 'right' }>`
  ${tw`flex flex-col min-w-0`}
  text-align: ${({ $align }) => $align ?? 'left'};
`;

const SummaryLabel = styled.span`
  ${tw`text-[11px] font-semibold uppercase`}
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary);
`;

const SummaryValue = styled.span<{ $muted?: boolean }>`
  ${tw`text-sm font-semibold truncate`}
  color: ${({ $muted }) => ($muted ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)')};
`;

const SummaryTime = styled.span`
  ${tw`text-xs`}
  color: var(--color-text-secondary);
`;

const Arrow = styled.span`
  color: var(--color-text-tertiary);
  font-size: 14px;
`;

const Body = styled.div`
  ${tw`px-5 pb-2 overflow-y-auto`}
`;

const Legend = styled.div`
  ${tw`flex flex-wrap gap-x-4 gap-y-1 px-5 pb-3 text-xs`}
  color: var(--color-text-secondary);
`;

const LegendItem = styled.span<{ $kind: 'blocked' | 'partial' }>`
  ${tw`inline-flex items-center gap-1.5`}
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: ${({ $kind }) =>
      $kind === 'blocked' ? 'var(--color-text-tertiary)' : 'var(--color-warning)'};
  }
`;

const Footer = styled.div`
  ${tw`flex items-center justify-between gap-3 px-5 py-3`}
  border-top: 1px solid var(--color-border);
  background: var(--color-bg-elevated);
`;

/* ---- Spec 028: the Months mode (left pane: how long; right pane: the calendar) ---- */

const ModeRow = styled.div`
  ${tw`flex items-center justify-between gap-3 px-5 pb-2 flex-wrap`}
`;

const Hint = styled.span`
  ${tw`text-sm`}
  color: var(--color-text-secondary);
  b {
    color: var(--color-text-primary);
    font-weight: 600;
  }
`;

const MonthsBody = styled.div`
  ${tw`grid gap-4 px-5 pb-3 overflow-y-auto`}
  grid-template-columns: minmax(0, 1fr);
  @media (min-width: 900px) {
    grid-template-columns: 250px minmax(0, 1fr);
  }
`;

const Pane = styled.div`
  ${tw`grid gap-3 content-start`}
`;

const PaneLabel = styled.div`
  ${tw`text-xs font-semibold uppercase`}
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary);
`;

const Rail = styled.div`
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) 36px;
  align-items: center;
  gap: 6px;
`;

const StepButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 999px;
  border: 1px solid var(--color-border);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: var(--color-text-secondary);
  &:hover:not(:disabled) {
    border-color: var(--color-text-primary);
    color: var(--color-text-primary);
  }
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

const Numbers = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  align-items: center;
  height: 72px;
  overflow: hidden;
`;

const Num = styled.div<{ $pos: 0 | 1 | 2 }>`
  text-align: center;
  font-family: var(--font-display);
  font-weight: 700;
  line-height: 1;
  color: ${({ $pos }) => ($pos === 0 ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)')};
  font-size: ${({ $pos }) => ($pos === 0 ? '52px' : $pos === 1 ? '30px' : '20px')};
  opacity: ${({ $pos }) => ($pos === 0 ? 1 : $pos === 1 ? 0.8 : 0.5)};
`;

const Unit = styled.div`
  ${tw`text-center font-semibold`}
  color: var(--color-text-primary);
`;

const Chips = styled.div`
  ${tw`flex flex-wrap gap-1.5`}
`;

const Chip = styled.button<{ $active: boolean }>`
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? 'var(--color-black)' : 'var(--color-border)')};
  background: ${({ $active }) => ($active ? 'var(--color-black)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--color-on-primary)' : 'var(--color-text-secondary)')};
  font-size: 12px;
  font-weight: 500;
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const Pill = styled.span`
  ${tw`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap`}
  background: rgb(var(--color-black-rgb) / 0.08);
  color: var(--color-text-primary);
`;

const TierLine = styled.div`
  ${tw`flex items-center gap-2 text-xs`}
  color: var(--color-text-secondary);
`;

const Facts = styled.div`
  ${tw`grid gap-2 px-3 py-3`}
  background: var(--color-bg-secondary);
  border-radius: var(--radius-md);
`;

const Fact = styled.div`
  display: grid;
  grid-template-columns: 62px minmax(0, 1fr);
  align-items: baseline;
  gap: 8px;
`;

const FactValue = styled.span<{ $muted?: boolean }>`
  ${tw`text-sm font-semibold`}
  color: ${({ $muted }) => ($muted ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)')};
  small {
    font-weight: 500;
    color: var(--color-text-secondary);
  }
`;

const DaysLine = styled.div`
  ${tw`flex items-center gap-2 flex-wrap pt-2 text-sm`}
  border-top: 1px solid var(--color-border);
  color: var(--color-text-secondary);
  b {
    color: var(--color-text-primary);
  }
`;

const Note = styled.div<{ $bad?: boolean }>`
  ${tw`flex gap-2 items-start rounded-md px-3 py-2.5 text-sm`}
  line-height: 1.4;
  background: ${({ $bad }) => ($bad ? 'rgb(220 38 38 / 0.1)' : 'var(--color-bg-secondary)')};
  color: ${({ $bad }) => ($bad ? 'var(--color-text-primary)' : 'var(--color-text-secondary)')};
  > span:first-child {
    flex: 0 0 auto;
    color: ${({ $bad }) => ($bad ? '#dc2626' : 'var(--color-text-tertiary)')};
  }
`;

const LinkButton = styled.button`
  ${tw`text-sm font-medium underline`}
  text-underline-offset: 3px;
  color: var(--color-text-primary);
`;

const CalendarPane = styled.div`
  ${tw`grid gap-2 content-start min-w-0`}
`;

const CalendarLegend = styled.div`
  ${tw`flex flex-wrap gap-x-4 gap-y-1 text-xs`}
  color: var(--color-text-secondary);
`;

const LegendDot = styled.i<{ $kind: 'start' | 'end' | 'range' | 'blocked' }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  margin-right: 5px;
  vertical-align: -1px;
  ${({ $kind }) =>
    $kind === 'start'
      ? 'background: var(--color-black);'
      : $kind === 'end'
        ? 'box-shadow: inset 0 0 0 2px var(--color-black);'
        : $kind === 'range'
          ? 'background: rgb(var(--color-black-rgb) / 0.14);'
          : 'background: var(--color-text-tertiary);'}
`;

const QUICK_MONTHS = [1, 2, 3, 6, 12];

const useWideViewport = (minWidth: number) => {
  const [wide, setWide] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= minWidth,
  );
  useEffect(() => {
    // jsdom has no matchMedia; the initial innerWidth answer stands there.
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(`(min-width: ${minWidth}px)`);
    const update = () => setWide(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, [minWidth]);
  return wide;
};

const tierHint = (months: number) =>
  months >= 3
    ? "The host's longest-trip rate."
    : months === 2
      ? 'Most hosts price 2 months lower per day.'
      : 'Add a month to reach the 2-month rate.';

export interface DateRangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  startDate: Date;
  endDate: Date;
  /** Spec 028: `pick` says how the guest chose (Months carries the month count). */
  onSave: (start: Date, end: Date, pick: TripPick) => void;
  /** Minimum selectable date (defaults to today). */
  minDate?: Date;
  /** Last selectable date (defaults to one year from the minimum). */
  maxDate?: Date;
  /** Legacy: whole days that cannot be picked. Prefer `blockedDays`. */
  disabledDates?: Date[];
  /** Legacy: blocked instants. Prefer `blockedDays` / `partialDays`. */
  disabledRanges?: Array<{ from: Date; to: Date }>;
  disablePastDates?: boolean;
  /** Zone used for "today"; day keys stay browser-local calendar dates. */
  timezone?: string | null;
  /** Fully blocked days (`yyyy-MM-dd`): not selectable, struck through. */
  blockedDays?: Set<string>;
  /** Partly blocked days: selectable, struck through with a warning dot. */
  partialDays?: Set<string>;
  /** Called once both days are chosen; a message rejects the pair (toast and reset). */
  validateRange?: (startKey: string, endKey: string) => string | null;
  /** Pickup is fixed (modify trip): only the return day moves. */
  lockedStart?: boolean;
  startTimeLabel?: string;
  endTimeLabel?: string;
  title?: string;
  /** Spec 028: show the Dates | Months switch (the brand and the car rent by the month). */
  allowMonths?: boolean;
  initialMode?: TripMode;
  initialMonths?: number;
}

export interface TripPick {
  mode: TripMode;
  months: number;
}

const keyOf = (date: Date | null | undefined): string | null => {
  if (!date || Number.isNaN(date.getTime())) return null;
  return DateTime.fromJSDate(date).toISODate();
};

const dateOf = (key: string): Date => DateTime.fromISO(key).toJSDate();

const formatDay = (key: string | null) =>
  key ? DateTime.fromISO(key).toFormat('ccc, MMM d') : null;

export const DateRangeModal: React.FC<DateRangeModalProps> = ({
  isOpen,
  onClose,
  startDate,
  endDate,
  onSave,
  minDate,
  maxDate,
  disabledDates,
  disabledRanges,
  disablePastDates = true,
  timezone,
  blockedDays,
  partialDays,
  validateRange,
  lockedStart = false,
  startTimeLabel,
  endTimeLabel,
  title = 'Trip dates',
  allowMonths = false,
  initialMode = 'dates',
  initialMonths = MIN_MONTHS,
}) => {
  const [range, setRange] = useState<RangeValue>({ start: keyOf(startDate), end: keyOf(endDate) });
  const [resetNonce, setResetNonce] = useState(0);
  // Spec 028: the Months mode. `months` is the count; the end is derived from the start.
  const [mode, setMode] = useState<TripMode>(allowMonths ? initialMode : 'dates');
  const [months, setMonths] = useState<number>(initialMonths);
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const twoMonths = useWideViewport(900);

  const todayKey = useMemo(
    () => (timezone ? DateTime.now().setZone(timezone) : DateTime.local()).toISODate() || '',
    [timezone],
  );
  const minKey = keyOf(minDate) ?? (disablePastDates ? todayKey : '1970-01-01');
  const maxKey = keyOf(maxDate) ?? DateTime.fromISO(minKey).plus({ years: 1 }).toISODate() ?? '';

  // Legacy Date lists become day sets in the browser zone, as they were compared before.
  const { blocked, partial } = useMemo(() => {
    if (blockedDays || partialDays) {
      return {
        blocked: blockedDays ?? new Set<string>(),
        partial: partialDays ?? new Set<string>(),
      };
    }
    const sets = computeBlockedDateSets({
      slots: (disabledRanges ?? []).map(r => ({
        startAt: r.from.getTime(),
        endAt: r.to.getTime(),
      })),
      fromKey: minKey,
      toKey: maxKey,
      timezone: DateTime.local().zoneName,
    });
    (disabledDates ?? []).forEach(date => {
      const key = keyOf(date);
      if (key) {
        sets.blocked.add(key);
        sets.partial.delete(key);
      }
    });
    return sets;
  }, [blockedDays, partialDays, disabledDates, disabledRanges, minKey, maxKey]);

  useEffect(() => {
    if (isOpen) {
      const start = keyOf(startDate);
      const nextMode: TripMode = allowMonths ? initialMode : 'dates';
      setMode(nextMode);
      setMonths(initialMonths);
      setHoverKey(null);
      setRange(
        nextMode === 'months' && start
          ? { start, end: monthlyEnd(start, initialMonths) }
          : { start, end: keyOf(endDate) },
      );
      setResetNonce(n => n + 1);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, startDate, endDate, allowMonths, initialMode, initialMonths]);

  const handleChange = (next: RangeValue) => {
    if (next.start && next.end && validateRange) {
      const message = validateRange(next.start, next.end);
      if (message) {
        toast.error(message);
        setResetNonce(n => n + 1);
        return;
      }
    }
    setRange(next);
  };

  // Spec 028: the months the current start allows (the trip must end inside the calendar).
  const monthsCap = range.start ? maxMonthsFrom(range.start, maxKey) : MAX_MONTHS;
  const clampMonths = (n: number, start: string | null) =>
    Math.max(
      MIN_MONTHS,
      Math.min(start ? maxMonthsFrom(start, maxKey) || MIN_MONTHS : MAX_MONTHS, n),
    );

  const pickMonths = (n: number) => {
    const next = clampMonths(n, range.start);
    setMonths(next);
    if (range.start) setRange({ start: range.start, end: monthlyEnd(range.start, next) });
  };

  // Months mode: a tap moves the start; the end follows.
  const handleMonthsTap = (next: RangeValue) => {
    const start = next.start;
    if (!start) return;
    const count = clampMonths(months, start);
    setMonths(count);
    setRange({ start, end: monthlyEnd(start, count) });
  };

  const switchMode = (next: TripMode) => {
    if (next === mode) return;
    setHoverKey(null);
    if (next === 'months') {
      const start = range.start ?? minKey;
      const count = clampMonths(
        range.end && range.start ? roundToMonths(daysBetween(range.start, range.end)) : months,
        start,
      );
      setMonths(count);
      setRange({ start, end: monthlyEnd(start, count) });
    }
    setMode(next);
  };

  const handleReset = () => {
    setHoverKey(null);
    if (mode === 'months') {
      const start = keyOf(startDate) ?? minKey;
      setMonths(initialMonths);
      setRange({ start, end: monthlyEnd(start, clampMonths(initialMonths, start)) });
      setResetNonce(n => n + 1);
      return;
    }
    setRange(lockedStart ? { start: range.start, end: null } : { start: null, end: null });
  };

  const problem =
    mode === 'months' && range.start && range.end
      ? monthlyProblem(range.start, range.end, months, blocked, maxKey)
      : null;
  const days = range.start && range.end ? daysBetween(range.start, range.end) : null;
  // Dates mode stays the ordinary picker (a daily trip is fine); the tier shows from 30 days.
  const monthlyDays = allowMonths && mode === 'dates' && days !== null && days >= MONTH_DAYS;

  const handleSave = () => {
    if (!range.start) return;
    if (mode === 'months') {
      if (!range.end || problem) return;
      if (validateRange) {
        const message = validateRange(range.start, range.end);
        if (message) {
          toast.error(message);
          return;
        }
      }
      onSave(dateOf(range.start), dateOf(range.end), { mode: 'months', months });
      onClose();
      return;
    }
    const endKey = range.end ?? range.start;
    onSave(dateOf(range.start), dateOf(endKey), { mode: 'dates', months });
    onClose();
  };

  const jumpToNextStart = (start: string) => {
    setRange({ start, end: monthlyEnd(start, months) });
    setResetNonce(n => n + 1);
  };

  const hasPartial = partial.size > 0;
  const hasBlocked = blocked.size > 0;
  const monthsShown = twoMonths ? 2 : 1;

  // Months mode: hovering a day previews the trip it would start.
  const preview =
    mode === 'months' && hoverKey && hoverKey !== range.start
      ? { start: hoverKey, end: monthlyEnd(hoverKey, clampMonths(months, hoverKey)) }
      : null;
  const painted = preview ?? range;
  const paintedBad =
    mode === 'months' && painted.start && painted.end
      ? monthlyProblem(painted.start, painted.end, months, blocked, maxKey) !== null
      : false;

  const modeTabs = allowMonths ? (
    <Tabs<TripMode>
      aria-label="Pick by"
      items={[
        { key: 'months', label: 'Months', 'data-testid': 'trip-mode-months' },
        { key: 'dates', label: 'Dates', 'data-testid': 'trip-mode-dates' },
      ]}
      value={mode}
      onChange={switchMode}
    />
  ) : null;

  const content = (
    <ModalOverlay
      $isOpen={isOpen}
      data-portal-safe="true"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <ModalContent $wide={mode === 'months'} data-testid="trip-dates-modal" data-mode={mode}>
        <Header>
          <Title>{mode === 'months' ? 'Monthly trip' : title}</Title>
          <CloseButton type="button" aria-label="Close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </Header>

        {mode === 'months' ? (
          <>
            <ModeRow>
              <Hint>
                Tap the <b>pickup day</b>. The return day follows.
              </Hint>
              {modeTabs}
            </ModeRow>
            <MonthsBody>
              <Pane>
                <PaneLabel>I want a car for</PaneLabel>
                <Rail>
                  <StepButton
                    type="button"
                    aria-label="One month fewer"
                    disabled={months <= MIN_MONTHS}
                    onClick={() => pickMonths(months - 1)}
                    data-testid="months-minus"
                  >
                    −
                  </StepButton>
                  <Numbers aria-hidden="true">
                    {[-2, -1, 0, 1, 2].map(offset => {
                      const n = months + offset;
                      const pos = Math.abs(offset) as 0 | 1 | 2;
                      return (
                        <Num key={offset} $pos={pos}>
                          {n >= MIN_MONTHS && n <= MAX_MONTHS ? n : ''}
                        </Num>
                      );
                    })}
                  </Numbers>
                  <StepButton
                    type="button"
                    aria-label="One month more"
                    disabled={months >= monthsCap}
                    onClick={() => pickMonths(months + 1)}
                    data-testid="months-plus"
                  >
                    +
                  </StepButton>
                </Rail>
                <Unit role="status" aria-live="polite" data-testid="months-count">
                  {monthsLabel(months)}
                </Unit>
                <Chips role="group" aria-label="Quick pick">
                  {QUICK_MONTHS.map(n => (
                    <Chip
                      key={n}
                      type="button"
                      $active={months === n}
                      aria-pressed={months === n}
                      disabled={n > monthsCap}
                      onClick={() => pickMonths(n)}
                      data-testid={`months-chip-${n}`}
                    >
                      {n} mo
                    </Chip>
                  ))}
                </Chips>
                <TierLine>
                  <Pill>{tierOf(days ?? MONTH_DAYS * months)}</Pill>
                  <span>{tierHint(months)}</span>
                </TierLine>
                <Facts>
                  <Fact>
                    <SummaryLabel>Pickup</SummaryLabel>
                    <FactValue $muted={!range.start}>
                      {range.start
                        ? DateTime.fromISO(range.start).toFormat('ccc, MMM d, yyyy')
                        : 'Tap a day'}
                      {startTimeLabel && range.start ? <small> · {startTimeLabel}</small> : null}
                    </FactValue>
                  </Fact>
                  <Fact>
                    <SummaryLabel>Return</SummaryLabel>
                    <FactValue $muted={!range.end}>
                      {range.end
                        ? DateTime.fromISO(range.end).toFormat('ccc, MMM d, yyyy')
                        : 'Follows the pickup'}
                      {range.end ? <small> · auto</small> : null}
                    </FactValue>
                  </Fact>
                  <DaysLine data-testid="months-days">
                    {days !== null ? (
                      <>
                        <b>{days} days</b>
                        <Pill>{tierOf(days)}</Pill>
                      </>
                    ) : (
                      <span>Tap a pickup day.</span>
                    )}
                  </DaysLine>
                </Facts>
                {problem?.kind === 'blocked' ? (
                  <Note $bad data-testid="months-problem">
                    <span aria-hidden="true">●</span>
                    <span>
                      Crosses a booked trip on{' '}
                      {DateTime.fromISO(problem.day).toFormat('MMM d, yyyy')}.{' '}
                      {problem.nextStart ? (
                        <>
                          Next free start for {monthsLabel(months)}:{' '}
                          <LinkButton
                            type="button"
                            onClick={() => jumpToNextStart(problem.nextStart as string)}
                            data-testid="months-next-start"
                          >
                            {DateTime.fromISO(problem.nextStart).toFormat('MMM d, yyyy')}
                          </LinkButton>
                          .
                        </>
                      ) : (
                        'Try fewer months.'
                      )}
                    </span>
                  </Note>
                ) : problem?.kind === 'short' ? (
                  <Note $bad data-testid="months-problem">
                    <span aria-hidden="true">●</span>
                    <span>Monthly trips are 30 days or more. This one is {problem.days}.</span>
                  </Note>
                ) : (
                  <Note>
                    <span aria-hidden="true">ⓘ</span>
                    <span>
                      A month is 30 days. Need a different end?{' '}
                      <LinkButton
                        type="button"
                        onClick={() => switchMode('dates')}
                        data-testid="months-adjust-end"
                      >
                        Adjust the end date
                      </LinkButton>
                      .
                    </span>
                  </Note>
                )}
              </Pane>
              <CalendarPane>
                {isOpen && (
                  <RangeCalendar
                    value={range}
                    onChange={handleMonthsTap}
                    mode="single"
                    minKey={minKey}
                    maxKey={maxKey}
                    todayKey={todayKey}
                    blocked={blocked}
                    partial={partial}
                    resetNonce={resetNonce}
                    monthsShown={monthsShown}
                    endStyle="outline"
                    preview={preview}
                    tone={paintedBad ? 'bad' : 'normal'}
                    onHoverDay={twoMonths ? setHoverKey : undefined}
                  />
                )}
                <CalendarLegend aria-hidden="true">
                  <span>
                    <LegendDot $kind="start" />
                    Pickup
                  </span>
                  <span>
                    <LegendDot $kind="end" />
                    Return (follows)
                  </span>
                  <span>
                    <LegendDot $kind="range" />
                    Your trip
                  </span>
                  {hasBlocked && (
                    <span>
                      <LegendDot $kind="blocked" />
                      Booked
                    </span>
                  )}
                </CalendarLegend>
              </CalendarPane>
            </MonthsBody>
          </>
        ) : (
          <>
            {modeTabs && (
              <ModeRow>
                <Hint>
                  {range.start && !range.end ? (
                    <>
                      Now tap the <b>return day</b>. From {MONTH_DAYS} days the trip is monthly.
                    </>
                  ) : (
                    <>
                      Tap the <b>pickup day</b>, then the return day.
                    </>
                  )}
                </Hint>
                {modeTabs}
              </ModeRow>
            )}
            <Summary>
              <SummaryCol>
                <SummaryLabel>Pickup</SummaryLabel>
                <SummaryValue $muted={!range.start}>
                  {formatDay(range.start) ?? 'Select a day'}
                </SummaryValue>
                {startTimeLabel && range.start && <SummaryTime>{startTimeLabel}</SummaryTime>}
              </SummaryCol>
              <Arrow aria-hidden="true">→</Arrow>
              <SummaryCol $align="right">
                <SummaryLabel>Return</SummaryLabel>
                <SummaryValue $muted={!range.end}>
                  {formatDay(range.end) ?? 'Select a day'}
                </SummaryValue>
                {endTimeLabel && range.end && <SummaryTime>{endTimeLabel}</SummaryTime>}
              </SummaryCol>
            </Summary>

            <Body>
              {isOpen && (
                <RangeCalendar
                  value={range}
                  onChange={handleChange}
                  minKey={minKey}
                  maxKey={maxKey}
                  todayKey={todayKey}
                  blocked={blocked}
                  partial={partial}
                  lockedStart={lockedStart}
                  resetNonce={resetNonce}
                />
              )}
              {monthlyDays && days !== null && (
                <TierLine style={{ paddingTop: 8 }} data-testid="dates-days">
                  <b style={{ color: 'var(--color-text-primary)' }}>{days} days</b>
                  <Pill>{tierOf(days)}</Pill>
                  <span>A monthly trip.</span>
                </TierLine>
              )}
            </Body>

            {(hasBlocked || hasPartial) && (
              <Legend aria-hidden="true">
                {hasBlocked && <LegendItem $kind="blocked">Unavailable</LegendItem>}
                {hasPartial && <LegendItem $kind="partial">Partly booked, check times</LegendItem>}
              </Legend>
            )}
          </>
        )}

        <Footer>
          <Button text="Reset" theme="text" type="button" onClick={handleReset} />
          <Button
            text={mode === 'months' ? 'Save' : 'Apply'}
            type="button"
            isDisabled={!range.start || (mode === 'months' && (!range.end || problem !== null))}
            onClick={handleSave}
          />
        </Footer>
      </ModalContent>
    </ModalOverlay>
  );

  return createPortal(content, document.body);
};
